import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { MagnifyingGlassIcon } from "@radix-ui/react-icons"
import { useMemo, useState } from "react"

import { AppShell } from "@/components/app-shell"
import { ConfirmDialog } from "@/components/confirm-dialog"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

function formatDate(value) {
  if (!value) return "-"

  return new Date(value).toLocaleString()
}

function statusClassName(status) {
  if (status === "ready") return "bg-[rgb(42_157_143/0.12)] text-[var(--color-verdigris)]"
  if (status === "failed") return "bg-[rgb(197_88_88/0.12)] text-[var(--color-danger)]"
  if (status === "generating") return "bg-[rgb(233_196_106/0.18)] text-[rgb(126_88_12)]"

  return "bg-[var(--color-panel)] text-[var(--color-text-muted)]"
}

function scoreLabel(value) {
  return value === null || value === undefined ? "-" : Number(value).toFixed(2)
}

function searchableText(evalCase) {
  return [
    evalCase.question,
    evalCase.expected_answer,
    evalCase.human_expected_answer,
    evalCase.canonical_expected_answer,
    evalCase.source_document_title,
    evalCase.source_page_number,
    ...Array.isArray(evalCase.citations) ? evalCase.citations.flatMap((citation) => [
      citation?.title,
      citation?.page,
      citation?.url
    ]) : []
  ].filter(Boolean).join(" ")
}

function CaseRow({ evalCase, index }) {
  const source = [
    evalCase.source_document_title,
    evalCase.source_page_number ? `page ${evalCase.source_page_number}` : null
  ].filter(Boolean).join(", ")

  return (
    <article className="rounded-lg border border-[var(--color-border)] bg-white p-4 shadow-[var(--shadow-soft)]">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-text-faint)]">
            Case {index + 1}
          </p>
          <h3 className="mt-2 text-base font-semibold leading-6 text-[var(--color-charcoal-blue)]">
            {evalCase.question}
          </h3>
        </div>

        {source ? (
          <span className="shrink-0 rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-medium text-[var(--color-text-muted)]">
            {source}
          </span>
        ) : null}
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-text-faint)]">Expected answer</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[var(--color-text-muted)]">
            {evalCase.expected_answer || "-"}
          </p>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-text-faint)]">Human override</p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[var(--color-text-muted)]">
            {evalCase.human_expected_answer || "-"}
          </p>
        </div>
      </div>

      {Array.isArray(evalCase.citations) && evalCase.citations.length > 0 ? (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-text-faint)]">Citations</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {evalCase.citations.map((citation, index) => {
              const label = [
                citation?.title,
                citation?.page ? `page ${citation.page}` : null
              ].filter(Boolean).join(", ")

              if (citation?.url) {
                return (
                  <a
                    className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-medium text-[var(--color-charcoal-blue)] hover:text-[var(--color-verdigris)]"
                    href={citation.url}
                    key={`${citation.url}-${index}`}
                  >
                    {label || "Open citation"}
                  </a>
                )
              }

              return (
                <span
                  className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-medium text-[var(--color-text-muted)]"
                  key={`${label}-${index}`}
                >
                  {label || "Citation"}
                </span>
              )
            })}
          </div>
        </div>
      ) : null}

      {evalCase.metadata?.generator_model ? (
        <p className="mt-4 text-xs text-[var(--color-text-faint)]">
          Generated by {evalCase.metadata.generator_model}
        </p>
      ) : null}
    </article>
  )
}

export default function BenchmarksPage({ datasets = [], selected_dataset = null }) {
  const { organization } = usePage().props
  const organizationBasePath = organization?.slug ? `/orgs/${organization.slug}` : ""
  const [query, setQuery] = useState("")
  const [deleteOpen, setDeleteOpen] = useState(false)
  const deleteForm = useForm({})
  const cases = selected_dataset?.cases || []
  const normalizedQuery = query.trim().toLowerCase()
  const filteredCases = useMemo(() => {
    if (!normalizedQuery) return cases

    return cases.filter((evalCase) => searchableText(evalCase).toLowerCase().includes(normalizedQuery))
  }, [cases, normalizedQuery])

  return (
    <>
      <Head title="Benchmarks" />

      <AppShell
        title="Benchmarks"
        subtitle="Explore generated benchmark datasets and the Q/A cases used to evaluate assistant methodology profiles."
        wide
      >
        <div className="grid gap-6 xl:grid-cols-[22rem_minmax(0,1fr)]">
          <aside className="rounded-[1.75rem] bg-white/82 p-4 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-[var(--color-charcoal-blue)]">Datasets</h2>
              <span className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-medium text-[var(--color-text-muted)]">
                {datasets.length}
              </span>
            </div>

            <div className="mt-4 space-y-2">
              {datasets.length > 0 ? datasets.map((dataset) => {
                const active = selected_dataset?.id === dataset.id

                return (
                  <Link
                    className={cn(
                      "block rounded-lg border p-3 transition-colors",
                      active
                        ? "border-[var(--color-charcoal-blue)] bg-[var(--color-charcoal-blue)] text-white"
                        : "border-[var(--color-border)] bg-white text-[var(--color-text-muted)] hover:bg-[var(--color-panel)]"
                    )}
                    href={`${organizationBasePath}/settings/benchmarks?dataset_id=${dataset.id}`}
                    key={dataset.id}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className={cn("text-sm font-semibold", active ? "text-white" : "text-[var(--color-charcoal-blue)]")}>
                        {dataset.name}
                      </p>
                      <span className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-[0.68rem] font-semibold uppercase",
                        active ? "bg-white/14 text-white" : statusClassName(dataset.status)
                      )}>
                        {dataset.status}
                      </span>
                    </div>
                    <p className={cn("mt-2 text-xs", active ? "text-white/72" : "text-[var(--color-text-faint)]")}>
                      {dataset.case_count} cases - {dataset.run_count || 0} runs - {formatDate(dataset.created_at)}
                    </p>
                  </Link>
                )
              }) : (
                <p className="rounded-lg border border-dashed border-[var(--color-border)] p-4 text-sm text-[var(--color-text-muted)]">
                  No benchmark datasets have been generated yet.
                </p>
              )}
            </div>
          </aside>

          <section className="min-w-0 rounded-[1.75rem] bg-white/82 p-5 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
            {selected_dataset ? (
              <>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-semibold text-[var(--color-charcoal-blue)]">{selected_dataset.name}</h2>
                      <span className={cn("rounded-full px-3 py-1 text-xs font-semibold uppercase", statusClassName(selected_dataset.status))}>
                        {selected_dataset.status}
                      </span>
                    </div>
                    {selected_dataset.description ? (
                      <p className="mt-2 max-w-3xl text-sm leading-6 text-[var(--color-text-muted)]">{selected_dataset.description}</p>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-right sm:grid-cols-3">
                    <div className="rounded-lg bg-[var(--color-panel)] px-3 py-2">
                      <p className="text-xs text-[var(--color-text-faint)]">Cases</p>
                      <p className="text-lg font-semibold text-[var(--color-charcoal-blue)]">{selected_dataset.case_count}</p>
                    </div>
                    <div className="rounded-lg bg-[var(--color-panel)] px-3 py-2">
                      <p className="text-xs text-[var(--color-text-faint)]">Shown</p>
                      <p className="text-lg font-semibold text-[var(--color-charcoal-blue)]">{filteredCases.length}</p>
                    </div>
                    <div className="rounded-lg bg-[var(--color-panel)] px-3 py-2">
                      <p className="text-xs text-[var(--color-text-faint)]">Updated</p>
                      <p className="text-xs font-medium text-[var(--color-charcoal-blue)]">{formatDate(selected_dataset.updated_at)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <Button onClick={() => setDeleteOpen(true)} type="button" variant="outline">
                    Delete benchmark
                  </Button>
                </div>

                {selected_dataset.eval_runs?.length > 0 ? (
                  <div className="mt-5 rounded-lg border border-[var(--color-border)] bg-white p-4">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-[var(--color-text-faint)]">Evaluation runs</h3>
                      <span className="text-xs text-[var(--color-text-muted)]">{selected_dataset.eval_runs.length} runs</span>
                    </div>

                    <div className="mt-3 overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead className="text-[var(--color-text-faint)]">
                          <tr>
                            <th className="px-2 py-2 font-semibold">Methodology</th>
                            <th className="px-2 py-2 font-semibold">Status</th>
                            <th className="px-2 py-2 font-semibold">Score</th>
                            <th className="px-2 py-2 font-semibold">Passed</th>
                            <th className="px-2 py-2 font-semibold">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selected_dataset.eval_runs.map((run) => (
                            <tr className="border-t border-[var(--color-border)]" key={run.id}>
                              <td className="px-2 py-2">
                                <Link
                                  className="font-medium text-[var(--color-charcoal-blue)] underline decoration-[rgb(42_157_143/0.35)] underline-offset-4 hover:text-[var(--color-verdigris)]"
                                  href={run.detail_url || `${organizationBasePath}/settings/eval-runs/${run.id}`}
                                >
                                  {run.methodology_label}
                                </Link>
                                <p className="mt-1 text-xs text-[var(--color-text-faint)]">{run.methodology_key}</p>
                              </td>
                              <td className="px-2 py-2">
                                <span className={cn("rounded-full px-2 py-0.5 text-[0.68rem] font-semibold uppercase", statusClassName(run.status))}>
                                  {run.status}
                                </span>
                              </td>
                              <td className="px-2 py-2">{scoreLabel(run.average_score)}</td>
                              <td className="px-2 py-2">{run.passed_cases ?? "-"} / {run.total_cases ?? "-"}</td>
                              <td className="px-2 py-2">{formatDate(run.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                <div className="mt-5 flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-white px-3 py-2">
                  <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-[var(--color-text-faint)]" />
                  <input
                    className="min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-[var(--color-charcoal-blue)] outline-none placeholder:text-[var(--color-text-faint)]"
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search questions, answers, sources, or pages"
                    value={query}
                  />
                </div>

                <div className="mt-5 space-y-3">
                  {filteredCases.length > 0 ? filteredCases.map((evalCase, index) => (
                    <CaseRow evalCase={evalCase} index={index} key={evalCase.id} />
                  )) : (
                    <p className="rounded-lg border border-dashed border-[var(--color-border)] p-5 text-sm text-[var(--color-text-muted)]">
                      No benchmark cases match the current search.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <p className="rounded-lg border border-dashed border-[var(--color-border)] p-5 text-sm text-[var(--color-text-muted)]">
                Generate a benchmark dataset from Assistant settings to explore cases here.
              </p>
            )}
          </section>
        </div>
      </AppShell>

      <ConfirmDialog
        confirmLabel="Delete benchmark"
        description="This will permanently delete the benchmark dataset, generated cases, and related evaluation runs."
        onConfirm={() => {
          if (!selected_dataset) return

          deleteForm.delete(`${organizationBasePath}/settings/benchmarks/${selected_dataset.id}`, { preserveScroll: true })
        }}
        onOpenChange={setDeleteOpen}
        open={deleteOpen}
        processing={deleteForm.processing}
        title={`Delete ${selected_dataset?.name || "benchmark"}?`}
      />
    </>
  )
}
